<template>
  <div class="p-grid">
    <div class="p-col-12" v-if="trainingAssessment">
      <Card>
        <template #title="">
          <div class="p-d-flex p-justify-between">
            {{ trainingAssessment.question?.title ?? "แบบประเมิน" }}
            <div>
              <BackButton />
            </div>
          </div>
        </template>
        <template #subtitle="">
          <StatusBadge
            :status="trainingAssessment.status"
            :approveOn="trainingAssessment.approveOn"
            :submitOn="trainingAssessment.submitOn"
          />
        </template>
        <template #content="">
          <div class="p-grid">
            <div class="p-col-12 p-text-bold" style="fontSize: 1.2rem">
              ข้อมูลผู้เข้าร่วม
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>เรื่องที่อบรม :</b>
              {{
                trainingAssessment.trainingAttendance?.trainingDocument?.course
                  ?.description ?? "N/A"
              }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-8">
              <b>เลขที่เอกสาร :</b>
              {{ trainingAssessment.trainingAttendance?.trainingDocumentId }}
            </div>

            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ชื่อ :</b>
              {{ employeeName }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ตำแหน่ง :</b>
              {{ trainingAssessment?.employee?.position }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>รหัส :</b>
              {{ trainingAssessment?.employee?.id }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>แผนก :</b>
              {{ trainingAssessment?.employee?.divisionDescription ?? "-" }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ฝ่าย :</b>
              {{ trainingAssessment?.employee?.departmentDescription ?? "-" }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>เพศ :</b>
              {{
                trainingAssessment?.employee?.sex
                  ? trainingAssessment?.employee.sex === "M"
                    ? "ชาย"
                    : "หญิง"
                  : "-"
              }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>วันที่อบรม :</b>
              {{ trainingDate }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>ชื่อสถาบัน :</b>
              {{
                trainingAssessment.trainingAttendance?.trainingDocument
                  ?.instructor1?.institute ?? "N/A"
              }}
            </div>
            <div class="p-col-12 p-md-6 p-lg-4">
              <b>สถานที่ :</b>
              {{
                trainingAssessment.trainingAttendance?.trainingDocument
                  ?.location ?? "N/A"
              }}
            </div>

            <div class="p-col-12">
              <QuestionPanel
                v-if="isEmployee"
                :questionItems="questionItems"
                v-model:assessmentItems="assessmentItems"
                v-model:finalScore="trainingAssessment.employeeScore"
                :readonly="isReadonly"
                :isSupervisor="isSupervisor"
                :isEmployee="isEmployee"
                v-model:isComplete="isQuestionComplete"
                v-model:onlyEmployee="onlyEmployee"
              ></QuestionPanel>
              <QuestionPanel
                v-else
                :questionItems="questionItems"
                v-model:assessmentItems="assessmentItems"
                v-model:finalScore="trainingAssessment.supervisorScore"
                :readonly="isReadonly"
                :isSupervisor="isSupervisor"
                :isEmployee="isEmployee"
                v-model:isComplete="isQuestionComplete"
              ></QuestionPanel>
            </div>
          </div>
        </template>
        <template #footer="">
          <div class="p-grid">
            <div class="p-col-12 p-formgroup-inline p-field">
              <Button
                label="บันทึก"
                icon="pi pi-save"
                @click="onSaveDocument"
                :disabled="isReadonly"
              ></Button>
              <span class="p-float-label p-ml-2" v-if="!noApproverRequired">
                <InputUser
                  id="supervisor"
                  v-model="trainingAssessment.supervisorId"
                  :disabled="isReadonly || !isEmployee"
                  style="width: 300px"
                  :levelMoreThan="trainingAssessment.employee?.level ?? '10'"
                ></InputUser>
                <label for="supervisor">ระบุผู้บังคับบัญชา</label>
              </span>
              <Button
                label="ส่งเอกสาร"
                class="p-ml-2 p-button-danger"
                icon="pi pi-send"
                @click="onSubmitDocument"
                :disabled="
                  isReadonly ||
                    !trainingAssessment.supervisorId ||
                    !isQuestionComplete
                "
                v-if="isEmployee && !noApproverRequired"
              ></Button>
              <Button
                label="ส่งเอกสารให้ HRD"
                class="p-ml-2 p-button-danger"
                icon="pi pi-send"
                @click="onPassToHrd"
                :disabled="isReadonly || !isQuestionComplete"
                v-if="isEmployee && noApproverRequired"
              ></Button>
              <Button
                label="อนุมัติเอกสาร"
                class="p-ml-2 p-button-danger"
                icon="pi pi-send"
                @click="onApproveDocument"
                :disabled="isReadonly || !isQuestionComplete"
                v-if="isSupervisor"
              ></Button>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import { usePlugin } from "@/plugins";
import { defineComponent, ref, onMounted, computed } from "vue";
import {
  AssessmentItem,
  Employee,
  TrainingAssessment,
  UpsertTrainingAssessmentRequest,
  GetTrainingAssessmentResponse,
} from "@/types";
import QuestionPanel from "@/components/QuestionPanel.vue";
import InputUser from "@/components/InputUser.vue";
import StatusBadge from "@/components/StatusBadge.vue";

export default defineComponent({
  name: "AssessmentScreen",
  components: {
    QuestionPanel,
    InputUser,
    StatusBadge,
  },
  setup() {
    const trainingAssessment = ref<TrainingAssessment | null>(null);
    const assessmentItems = ref<AssessmentItem[]>([]);
    const selectedUser = ref<Employee[]>([]);
    const {
      setLoading,
      axios,
      error,
      success,
      route,
      useState,
      dayjs,
      formatAppDate,
      useSessionState,
      confirmation,
    } = usePlugin();
    const state = useState();
    const sessionState = useSessionState();
    const loadDocument = async () => {
      setLoading(true);
      try {
        const employeeId =
          route.params.employeeId ?? state.value.authentication.username;
        const result = await axios.get<GetTrainingAssessmentResponse>(
          `api/v1/services/trainingAssessment/${route.params.documentId}/${route.params.questionId}/${employeeId}`
        );
        trainingAssessment.value = result.data.trainingAssessment;
        assessmentItems.value =
          isEmployee.value === true
            ? trainingAssessment.value.assessmentData
              ? JSON.parse(trainingAssessment.value.assessmentData)
              : []
            : trainingAssessment.value.supervisorAssessmentData
            ? JSON.parse(trainingAssessment.value.supervisorAssessmentData)
            : JSON.parse(trainingAssessment.value.assessmentData ?? "[]"); //copy employee comment first.
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const noApproverRequired = computed(() => {
      return (
        sessionState.value.profile.noApproverLevel <=
          parseInt(trainingAssessment.value?.employee?.level ?? "0") ||
        onlyEmployee.value
      );
    });
    const questionItems = computed(() =>
      JSON.parse(trainingAssessment.value?.question?.body ?? "[]")
    );
    const onlyEmployee = ref(false);

    const onSaveDocument = async () => {
      if (trainingAssessment.value === null) {
        error("ไม่สามารถบันทึกเอกสารได้");
        return;
      }
      setLoading(true);
      try {
        //Setup for trainee assessment.
        if (isEmployee.value === true) {
          trainingAssessment.value.assessmentData = JSON.stringify(
            assessmentItems.value
          );
        }
        if (isSupervisor.value === true) {
          trainingAssessment.value.supervisorAssessmentData = JSON.stringify(
            assessmentItems.value
          );
        }
        await axios.post("api/v1/services/trainingAssessment/upsert", {
          trainingAssessment: trainingAssessment.value,
        } as UpsertTrainingAssessmentRequest);
        success("บันทึกข้อมูลสำเร็จแล้ว");
        loadDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSubmitDocument = async () => {
      confirmation.require({
        message: "กรุณายืนยันเพื่อทำการส่งเอกสารให้หัวหน้งาน",
        header: "ยืนยันส่งเอกสารให้หัวหน้างาน",
        acceptLabel: "ยืนยันส่งเอกสาร",
        rejectLabel: "ยกเลิก",
        accept: async () => {
          trainingAssessment.value!.submitOn = dayjs().toDate();
          await onSaveDocument();
        },
      });
    };

    const onPassToHrd = async () => {
      confirmation.require({
        message: "กรุณายืนยันเพื่อทำการส่งเอกสารให้ HRD",
        header: "ยืนยันส่งเอกสารให้ HRD",
        acceptLabel: "ยืนยันส่งเอกสาร",
        rejectLabel: "ยกเลิก",
        accept: async () => {
          trainingAssessment.value!.submitOn = dayjs().toDate();
          trainingAssessment.value!.approveOn = dayjs().toDate();
          await onSaveDocument();
        },
      });
    };

    const onApproveDocument = async () => {
      confirmation.require({
        message: "กรุณายืนยันเพื่อทำอนุมัติและ ส่งเอกสารให้ HRD",
        header: "ยืนยันอนุมัติและส่งเอกสารให้ HRD",
        acceptLabel: "อนุมัติ และส่งเอกสารให้ HRD",
        rejectLabel: "ยกเลิก",
        accept: async () => {
          trainingAssessment.value!.approveOn = dayjs().toDate();
          await onSaveDocument();
        },
      });
    };

    const trainingDate = computed(() => {
      return trainingAssessment.value?.trainingAttendance?.trainingDocument
        ?.startDate ===
        trainingAssessment.value?.trainingAttendance?.trainingDocument?.endDate
        ? formatAppDate(
            trainingAssessment.value?.trainingAttendance?.trainingDocument
              ?.startDate
          )
        : `${formatAppDate(
            trainingAssessment.value?.trainingAttendance?.trainingDocument
              ?.startDate
          )} - ${formatAppDate(
            trainingAssessment.value?.trainingAttendance?.trainingDocument
              ?.endDate
          )}`;
    });
    const employeeName = computed(() => {
      return `${trainingAssessment.value?.employee?.prename}${trainingAssessment.value?.employee?.name} ${trainingAssessment.value?.employee?.surname}`;
    });

    const isReadonly = computed(() => {
      const username = state.value.authentication.username;
      if (trainingAssessment.value === null) return true;
      //Not yet pass active date unable to fill
      if (
        dayjs().diff(trainingAssessment.value.activeDate ?? Date.now(), "d") < 0
      )
        return true;
      if (
        trainingAssessment.value.employeeId === username &&
        trainingAssessment.value.submitOn === null
      )
        return false;
      if (
        trainingAssessment.value.supervisorId === username &&
        trainingAssessment.value.submitOn !== null &&
        trainingAssessment.value.approveOn === null
      )
        return false;
      return true;
    });

    const isSubmitted = computed(
      () => trainingAssessment?.value?.submitOn !== null
    );
    const isApproved = computed(
      () => trainingAssessment?.value?.approveOn !== null
    );

    const isEmployee = computed(() => {
      return (
        trainingAssessment.value?.employeeId ===
        state.value.authentication.username
      );
    });

    const isSupervisor = computed(() => {
      return (
        trainingAssessment.value?.supervisorId ===
        state.value.authentication.username
      );
    });

    const isSelected = (questionId: number) => {
      return questionId === trainingAssessment.value?.questionId;
    };

    const isQuestionComplete = ref(false);

    onMounted(() => loadDocument());

    return {
      trainingAssessment,
      questionItems,
      assessmentItems,
      selectedUser,
      onSaveDocument,
      onSubmitDocument,
      onApproveDocument,
      onPassToHrd,

      trainingDate,
      employeeName,

      isSubmitted,
      isApproved,

      isReadonly,
      isEmployee,
      isSupervisor,

      isQuestionComplete,
      onlyEmployee,

      formatAppDate,
      isSelected,

      sessionState,
      noApproverRequired,
    };
  },
});
</script>

<style></style>
